import { Column } from 'primereact/column';
import { DataTable, type DataTableStateEvent } from 'primereact/datatable';
import { useMemo, type ReactNode } from 'react';

import { SHARED_COMPONENTS_TEXT } from '../constants';

const PAGE_SIZES = [25, 50, 100] as const;

export interface TableColumn<T> {
  key: string;
  header: string;
  /** Cell content; defaults to `String(row[key])`. */
  body?: (row: T) => ReactNode;
  /** API sort field; omit to make the column unsortable. */
  sortField?: string;
  width?: string;
  align?: 'left' | 'right' | 'center';
  /** Header content in place of the text (e.g. a select-all checkbox); `header` stays its name. */
  headerContent?: ReactNode;
  /** Shown before the running # column (a row checkbox, as in the prototype). */
  leading?: boolean;
}

export interface TableSort {
  field: string;
  order: 'asc' | 'desc';
}

export interface BaseTableProps<T extends object> {
  rows: T[];
  columns: TableColumn<T>[];
  /** Row id field; a dotted path (`client.id`) reaches into nested objects. */
  dataKey: (keyof T & string) | `${keyof T & string}.${string}`;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  sort?: TableSort | null;
  onSortChange?: (sort: TableSort | null) => void;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyMessage?: string;
  /** Running # column, like every prototype list. */
  showSerial?: boolean;
  /**
   * PrimeReact memoises rows, so cells that read page state (a selection, a lookup)
   * would show stale values. Pass anything that changes with that state to re-render.
   */
  refresh?: unknown;
  /** Extra classes per row (e.g. unread notifications are shaded). */
  rowClassName?: (row: T) => string;
  'aria-label'?: string;
}

function primitive(v: unknown): ReactNode {
  return typeof v === 'string' || typeof v === 'number' ? v : '';
}

function column<T>(c: TableColumn<T>) {
  return (
    <Column
      key={c.key}
      header={c.headerContent ?? c.header}
      field={c.sortField ?? c.key}
      sortable={Boolean(c.sortField)}
      style={c.width ? { width: c.width } : undefined}
      align={c.align}
      body={(row: T) => (c.body ? c.body(row) : primitive((row as Record<string, unknown>)[c.key]))}
    />
  );
}

/** Server-side paged/sorted table. Header click sorts A→Z then Z→A then off. */
export function BaseTable<T extends object>({
  rows,
  columns,
  dataKey,
  total,
  page,
  pageSize,
  onPageChange,
  sort,
  onSortChange,
  onRowClick,
  loading,
  emptyMessage = SHARED_COMPONENTS_TEXT.table.empty,
  showSerial = true,
  refresh,
  rowClassName,
  'aria-label': ariaLabel,
}: BaseTableProps<T>) {
  const first = (page - 1) * pageSize;
  // PrimeReact compares rows deeply before re-rendering them, so the refresh value is
  // stamped onto each row: when it changes, every row renders again.
  const shown = useMemo(
    () => (refresh === undefined ? rows : rows.map((r) => ({ ...r, __refresh: refresh }))),
    [rows, refresh],
  );

  const handleSort = (e: DataTableStateEvent) => {
    if (!onSortChange) return;
    const field = typeof e.sortField === 'string' ? e.sortField : '';
    onSortChange(field && e.sortOrder ? { field, order: e.sortOrder === 1 ? 'asc' : 'desc' } : null);
  };

  return (
    <DataTable
      value={shown}
      dataKey={dataKey}
      lazy
      // Shown whenever there is more than the smallest page, so a large page size (e.g. 100
      // with 74 rows) can always be changed back.
      paginator={total > PAGE_SIZES[0]}
      rows={pageSize}
      first={first}
      totalRecords={total}
      rowsPerPageOptions={[...PAGE_SIZES]}
      paginatorTemplate={{
        layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
        RowsPerPageDropdown: (opts: { element: ReactNode }) => (
          <span className="ml-3 flex items-center gap-2 text-[11.5px] text-text-3">
            <span aria-hidden>{SHARED_COMPONENTS_TEXT.table.rowsPerPage}</span>
            {opts.element}
          </span>
        ),
      }}
      // Same treatment as BaseSelect: name the rows-per-page box, hide its native twin.
      pt={{
        paginator: {
          RPPDropdown: {
            select: { 'aria-hidden': true },
            input: { 'aria-label': SHARED_COMPONENTS_TEXT.table.rowsPerPage },
            trigger: { 'aria-label': SHARED_COMPONENTS_TEXT.table.rowsPerPage },
          },
        },
      }}
      onPage={(e) => {
        onPageChange(Math.floor(e.first / e.rows) + 1, e.rows);
      }}
      sortField={sort?.field}
      sortOrder={sort ? (sort.order === 'asc' ? 1 : -1) : 0}
      removableSort
      onSort={handleSort}
      onRowClick={
        onRowClick
          ? (e) => {
              onRowClick(e.data as T);
            }
          : undefined
      }
      rowClassName={(row: T) =>
        [onRowClick ? 'cursor-pointer' : '', rowClassName?.(row) ?? ''].join(' ').trim()
      }
      loading={loading}
      emptyMessage={emptyMessage}
      rowHover
      size="small"
      aria-label={ariaLabel}
    >
      {columns.filter((c) => c.leading).map(column)}
      {showSerial && (
        <Column
          header="#"
          headerClassName="w-11"
          bodyClassName="tabular text-[11.5px] text-text-3"
          body={(_row: T, opts: { rowIndex: number }) => opts.rowIndex + 1}
        />
      )}
      {columns.filter((c) => !c.leading).map(column)}
    </DataTable>
  );
}
